import { Suspense } from 'react';

import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useParams } from 'react-router-dom';

import classNames from 'classnames';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants/messages';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { LoadingState } from '@/shared/ui/states';

import { useDagDetailTabsStore } from '@/entities/dag';

import { DagDetailPageHeader, DagDetailSummaryCard, DagDetailTabs } from '@/widgets/dag-detail';

import styles from './DagDetailPage.module.css';

const HEADER_SUSPENSE_FALLBACK = (
  <div className={styles.loadingContainer}>
    <LoadingState compact />
  </div>
);

const renderDagDetailErrorFallback = (fallbackProps: FallbackProps) => (
  <ErrorFallback
    className={styles.errorContainer}
    variant="compact"
    title={NOTIFICATION_MESSAGES.DAG_DETAIL_ERROR}
    description={NOTIFICATION_MESSAGES.DAG_DETAIL_ERROR_DESC}
    {...fallbackProps}
  />
);

export const DagDetailPage = () => {
  const params = useParams<{ dagId?: string }>();
  const dagUuid = params.dagId ?? '';
  const isExpanded = useDagDetailTabsStore((state) => state.isExpanded);

  return (
    <div className={styles.dagDetailPage}>
      <div className={styles.contentWrapper}>
        <div
          className={classNames(styles.headerContainer, {
            [styles.headerContainerCollapsed]: isExpanded,
            [styles.headerContainerExpanded]: !isExpanded,
          })}
          aria-hidden={isExpanded}>
          <div className={styles.headerSlot}>
            <ErrorBoundary fallbackRender={renderDagDetailErrorFallback}>
              <Suspense fallback={HEADER_SUSPENSE_FALLBACK}>
                <DagDetailPageHeader dagUuid={dagUuid} />
                <DagDetailSummaryCard />
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>

        <div className={styles.tabsSlot}>
          <ErrorBoundary fallbackRender={renderDagDetailErrorFallback}>
            <DagDetailTabs />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};
